import { FC, Fragment } from "react";
import { observer } from "mobx-react";

import { isFailure, isOk, isPending } from "@/common/common";
import { Alert } from "@/components-v2/Alert";
import { Box, Grid, VBox } from "@/components-v2/Box";
import { LinkButton } from "@/components-v2/Button";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { Dialog } from "@/components-v2/Dialog";
import { Spinner } from "@/components-v2/LoadingIndicator";
import { Tag } from "@/components-v2/Tag";
import { useDispatch, useRootStore, useTranslation } from "@/stores/RootStoreContext";

import {
  canImport,
  cancelCompanyImport,
  CompanyImportState,
  RejectedRow,
  readyCount,
  rejectionMessage,
  rowKey,
  rowLabel,
  submitCompanyImport,
  toggleAllAdmins,
} from "./companyImportDomain";

export const CompanyImportDialog: FC = observer(() => {
  const state = useRootStore().companyImport;
  return state ? <CompanyImportDialogView state={state} /> : null;
});

const CompanyImportDialogView: FC<{ state: CompanyImportState }> = observer(
  function CompanyImportDialogView({ state }: { state: CompanyImportState }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { previewResult, importResult } = state;
    return (
      <Dialog
        title={{
          key: isOk(previewResult) ? "company-import.finalise-title" : "company-import.title",
        }}
        maxWidth={"sm"}
        onCancel={() => dispatch([cancelCompanyImport()])}
        onSubmit={() => dispatch(submitCompanyImport(state))}
        isSubmitDisabled={!canImport(state)}
        isSubmitting={isPending(importResult)}
        submitLabel={{ key: "company-import.submit", params: { count: readyCount(state) } }}
      >
        <VBox gap={"lg"}>
          {isPending(previewResult) && (
            <Box alignItems={"center"} gap={"sm"}>
              <Spinner />
              {t("company-import.reading-file")}
            </Box>
          )}
          {isFailure(previewResult) && <Alert message={previewResult.error} type={"error"} />}
          {isOk(previewResult) && <ImportPreviewDetails state={state} />}
          {importResult && isFailure(importResult) && (
            <Alert message={importResult.error} type={"error"} />
          )}
        </VBox>
      </Dialog>
    );
  },
);

const ImportPreviewDetails: FC<{ state: CompanyImportState }> = observer(
  function ImportPreviewDetails({ state }: { state: CompanyImportState }) {
    const { t } = useTranslation();
    const { previewResult } = state;
    if (!isOk(previewResult)) return null;
    const { ready, rejected } = previewResult.data;
    return (
      <>
        <VBox gap={"sm"}>
          <Box fontWeight={"bold"}>
            {t({ key: "company-import.ready", params: { count: ready.length } })}
          </Box>
          <Grid cols={2} gap={"md"}>
            {ready.map((row) => (
              <Fragment key={rowKey(row)}>
                <Box>{row.name}</Box>
                <Box fg={"subtle"}>{row.organizationNumber}</Box>
              </Fragment>
            ))}
          </Grid>
        </VBox>

        {rejected.length > 0 && (
          <VBox gap={"sm"}>
            <Box fontWeight={"bold"} fg={"error"}>
              {t({ key: "company-import.rejected", params: { count: rejected.length } })}
            </Box>
            <Grid cols={2} gap={"md"}>
              {rejected.map((row) => (
                <RejectedRowView key={rowKey(row)} row={row} />
              ))}
            </Grid>
            {rejected.some((row) => row.rejection.code === "SUBSCRIPTION_UNAVAILABLE") && (
              <Alert message={{ key: "company-import.subscription-unavailable" }} type={"info"} />
            )}
          </VBox>
        )}

        {state.adminsModel && (
          <VBox gap={"md"}>
            <Box fontWeight={"bold"}>{t("company-import.administrators")}</Box>
            <Box>
              <LinkButton
                label={{ key: "add-company.select-all-none" }}
                onClick={() => state.adminsModel && toggleAllAdmins(state.adminsModel)}
              />
            </Box>
            <MCheckboxGroup
              model={state.adminsModel}
              labelFn={(admin) => (
                <Box alignItems={"center"} gap={"sm"}>
                  {admin.name}
                  {admin.isAccountAdmin && (
                    <Tag label={{ key: "enterprise-account.account-admin" }} color={"neutral"} />
                  )}
                </Box>
              )}
            />
          </VBox>
        )}

        <VBox gap={"md"}>
          <Box fontWeight={"bold"}>{t("add-company.terms-and-conditions")}</Box>
          <Checkbox
            label={{ key: "company-import.accept-terms", params: { count: readyCount(state) } }}
            checked={state.acceptTerms.value}
            onChange={(e) => state.acceptTerms.setValue(e.target.checked)}
          />
        </VBox>
      </>
    );
  },
);

const RejectedRowView: FC<{
  row: RejectedRow;
}> = ({ row }) => {
  const { t } = useTranslation();
  return (
    <>
      <VBox>
        {row.name && <Box>{row.name}</Box>}
        <Box fg={"subtle"}>{t(rowLabel(row))}</Box>
      </VBox>
      <Box fg={"subtle"}>{t(rejectionMessage(row.rejection))}</Box>
    </>
  );
};
